import DeviceDataTable from '@components/_shared/DeviceDataTable/DeviceDataTable';
import UIBox from '@components/_shared/UIBox';
import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import { useCurrentTrackerDeviceContext } from '@hooks/useCurrentTrackerDevice';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import QueryKeys from '@src/constants/queryKeys';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import { useEffect, useMemo } from 'react';

export default function MasterTrackerList() {
  const { currentTrackerDevice, setCurrentTrackerDevice } =
    useCurrentTrackerDeviceContext();
  const { deviceId: currentTrackerId } = currentTrackerDevice || {
    deviceId: 'master_tracker_1',
  };
  const {
    data: widgetConfig,
    isLoading: isLoadingWidgetData,
    isError: isErrorOnWidgetData,
  } = useGetInternalWidgetConfig<WidgetTypeDeviceList>(
    'masterTrackerList',
    {},
    {
      queryKeys: [QueryKeys.masterTrackerList],
    },
  );

  const memoizedDevices = useMemo(
    () => widgetConfig?.devices || [],
    [widgetConfig?.devices],
  );

  useEffect(() => {
    if (!currentTrackerDevice && memoizedDevices.length > 0) {
      setCurrentTrackerDevice(memoizedDevices[0]);
    }
  }, [currentTrackerDevice, memoizedDevices, setCurrentTrackerDevice]);

  const memoizedDataPoints = useMemo(
    () => widgetConfig?.dataPoints || [],
    [widgetConfig?.dataPoints],
  );

  const memoizedPointConfigRefs = useMemo(
    () => widgetConfig?.pointConfigRefs || [],
    [widgetConfig?.pointConfigRefs],
  );

  const handleRowSelection = (newTrackerId: string | undefined) => {
    if (newTrackerId) {
      const selectedDevice = memoizedDevices.find(
        (device) => device.deviceId === newTrackerId,
      );

      if (selectedDevice !== undefined) {
        setCurrentTrackerDevice(selectedDevice);
      }
    }
  };

  return (
    <UIBox
      header={widgetConfig?.header}
      isLoading={isLoadingWidgetData}
      isError={isErrorOnWidgetData}
    >
      <DeviceDataTable
        widgetId='master-trackers-list'
        hideFooter
        devices={memoizedDevices}
        dataPoints={memoizedDataPoints}
        pointConfigRefs={memoizedPointConfigRefs}
        useDualListView={widgetConfig?.useDualListView ?? false}
        rowSelectionModel={[currentTrackerId]}
        onRowSelectionModelChange={(newSelection: GridRowSelectionModel) =>
          handleRowSelection(newSelection[0] as string | undefined)
        }
        getRowClassName={() => 'MuiDataGrid-clickable'}
      />
    </UIBox>
  );
}
